import { useMemo } from 'react';

import { type AsyncState, isInYearMonth } from '../common';
import { useCurrentUser } from '../current-user';
import type { PendingEntry } from '../worklog/models';
import { type DayCell, pendingNetHours, type YearMonth } from './models';

export type AllocationStats = {
    capacity: number;
    target: number;
    targetMinRatio: number;
    targetMaxRatio: number;
    allocated: number;
    saved: number;
    pending: number;
    missing: number;
};

export type LegendCounts = {
    issues: number;
    bugs: number;
    requests: number;
    holidays: number;
    daysOff: number;
};

export type MonthStats = {
    stats: AllocationStats;
    pendingForMonth: PendingEntry[];
    legendCounts: LegendCounts;
};

export const useMonthStats = (cellsState: AsyncState<DayCell[]>, pending: PendingEntry[], currentMonth: YearMonth): MonthStats => {
    const { user } = useCurrentUser();
    const targetMinRatio = user?.targetMinRatio ?? 0.85;
    const targetMaxRatio = user?.targetMaxRatio ?? 0.9;

    const pendingForMonth = useMemo(
        () => pending.filter((p) => isInYearMonth(p.date, currentMonth.year, currentMonth.month)),
        [pending, currentMonth],
    );

    const stats = useMemo<AllocationStats>(() => {
        if (cellsState.status !== 'success') {
            return { capacity: 0, target: 0, targetMinRatio, targetMaxRatio, allocated: 0, saved: 0, pending: 0, missing: 0 };
        }
        const inMonth = cellsState.data.filter((c) => c.inMonth);
        const capacity = inMonth.reduce((sum, c) => sum + c.capacity, 0);
        const saved = inMonth.reduce((sum, c) => sum + c.savedHours, 0);
        const pendingNet = pendingNetHours(pendingForMonth);
        const allocated = Math.max(0, saved + pendingNet);
        const target = capacity * targetMaxRatio;
        const missing = Math.max(0, capacity - allocated);
        return { capacity, target, targetMinRatio, targetMaxRatio, allocated, saved, pending: pendingNet, missing };
    }, [cellsState, pendingForMonth, targetMinRatio, targetMaxRatio]);

    const legendCounts = useMemo<LegendCounts>(() => {
        const issueIds = new Set<number>();
        const bugIds = new Set<number>();
        const requestIds = new Set<number>();
        let holidays = 0;
        let daysOff = 0;

        if (cellsState.status === 'success') {
            for (const cell of cellsState.data) {
                if (!cell.inMonth) {
                    continue;
                }
                if (cell.nonWorking?.kind === 'holiday') {
                    holidays += 1;
                }
                if (cell.nonWorking?.kind === 'dayOff') {
                    daysOff += 1;
                }
                for (const entry of cell.savedEntries) {
                    const t = entry.workItemType.trim().toLowerCase();
                    if (t === 'bug') {
                        bugIds.add(entry.issueId);
                    } else if (t === 'request') {
                        requestIds.add(entry.issueId);
                    } else {
                        issueIds.add(entry.issueId);
                    }
                }
            }
        }

        for (const p of pendingForMonth) {
            const t = p.workItemType.trim().toLowerCase();
            if (t === 'bug') {
                bugIds.add(p.workItemId);
            } else if (t === 'request') {
                requestIds.add(p.workItemId);
            } else {
                issueIds.add(p.workItemId);
            }
        }

        return { issues: issueIds.size, bugs: bugIds.size, requests: requestIds.size, holidays, daysOff };
    }, [cellsState, pendingForMonth]);

    return { stats, pendingForMonth, legendCounts };
};
